import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import {
  ClinicApiService,
  DoctorProgressResponse,
  ActiveRequest,
  RequestType,
  SignupProgress,
  DoctorInfo,
} from "../../features/clinic/api/ClinicApiService";
import { DOCTOR_LOGOUT_EVENT } from "../utils/events/DoctorLogoutEvent";
import { isMockEnabled, getMockProgress } from "../dev/mockData";
import { createLatestResponseGate } from "../utils/latestResponseGate";

// Event name for triggering progress refresh
export const PROGRESS_REFRESH_EVENT = "doctor-progress-refresh";

// Helper to trigger refresh from anywhere in the app
export const triggerProgressRefresh = () => {
  window.dispatchEvent(new CustomEvent(PROGRESS_REFRESH_EVENT));
};

interface ProgressContextType {
  // State
  progress: DoctorProgressResponse | null;
  isLoading: boolean;
  error: string | null;
  lastFetched: number;

  // Doctor info (replaces /me)
  doctorInfo: DoctorInfo | null;
  signup: SignupProgress | null;

  // Clinic info
  clinicId: string | null;
  hasClinic: boolean;

  // Actions
  refreshProgress: (force?: boolean) => Promise<DoctorProgressResponse | null>;
  clearError: () => void;

  // Helpers
  getRequestByType: (type: RequestType) => ActiveRequest | undefined;
  getRequestById: (id: string) => ActiveRequest | undefined;
  hasActiveRequest: (type: RequestType) => boolean;

  // Signup status helpers
  isProfileComplete: boolean;
  isDocumentsComplete: boolean;
  currentSignupStage: string | null;
}

const ProgressContext = createContext<ProgressContextType | undefined>(undefined);

// Cache duration: 2 minutes
const CACHE_DURATION = 2 * 60 * 1000;

interface ProgressProviderProps {
  children: ReactNode;
}

export const ProgressProvider: React.FC<ProgressProviderProps> = ({ children }) => {
  const [progress, setProgress] = useState<DoctorProgressResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState(0);

  const clinicApi = useMemo(() => new ClinicApiService(), []);
  // Several screens fetch /progress at once (the per-screen hooks), so responses can arrive out of
  // order: an older one must not overwrite newer progress (see latestResponseGate).
  const responseGate = useMemo(() => createLatestResponseGate(), []);
  const latestProgress = useRef<DoctorProgressResponse | null>(null);

  const refreshProgress = useCallback(
    async (force = false): Promise<DoctorProgressResponse | null> => {
      // Dev mock mode - return mock data without API call
      if (isMockEnabled()) {
        const mockData = getMockProgress();
        setProgress(mockData);
        setLastFetched(Date.now());
        setIsLoading(false);
        return mockData;
      }

      // Check cache unless forced
      const now = Date.now();
      if (!force && progress && now - lastFetched < CACHE_DURATION) {
        return progress;
      }

      // Don't fetch if already loading — unless forced: a forced refresh must see the server as it is
      // now, and the request already in flight may have started before the change the caller waits for.
      if (isLoading && !force) {
        return progress;
      }

      setIsLoading(true);
      setError(null);

      const ticket = responseGate.begin();
      try {
        const result = await clinicApi.getDoctorProgress();

        if (result.isSuccess && result.data) {
          if (!responseGate.shouldApply(ticket)) {
            return latestProgress.current;
          }
          latestProgress.current = result.data;
          setProgress(result.data);
          setLastFetched(Date.now());
          return result.data;
        } else {
          setError(result.error || "Failed to fetch progress");
          return null;
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to fetch progress";
        setError(errorMessage);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [clinicApi, isLoading, lastFetched, progress, responseGate]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Helper: Get request by type
  const getRequestByType = useCallback(
    (type: RequestType): ActiveRequest | undefined => {
      return progress?.active_requests.find((r) => r.request_type === type);
    },
    [progress]
  );

  // Helper: Get request by ID
  const getRequestById = useCallback(
    (id: string): ActiveRequest | undefined => {
      return progress?.active_requests.find((r) => r.request_id === id);
    },
    [progress]
  );

  // Helper: Check if has active request of type
  const hasActiveRequest = useCallback(
    (type: RequestType): boolean => {
      return progress?.active_requests.some((r) => r.request_type === type) || false;
    },
    [progress]
  );

  // Derived state from progress
  const doctorInfo = progress?.doctor_info || null;
  const signup = progress?.signup || null;
  const clinicId = progress?.clinic_id || null;
  const hasClinic = !!progress?.clinic_id;
  const isProfileComplete = progress?.signup?.profile_completed || false;
  const isDocumentsComplete = progress?.signup?.documents_complete || false;
  const currentSignupStage = progress?.signup?.current_stage || null;

  // Listen for refresh events
  useEffect(() => {
    const handleRefresh = () => {
      refreshProgress(true);
    };

    window.addEventListener(PROGRESS_REFRESH_EVENT, handleRefresh);
    return () => {
      window.removeEventListener(PROGRESS_REFRESH_EVENT, handleRefresh);
    };
  }, [refreshProgress]);

  // Listen for logout events to clear progress data
  useEffect(() => {
    const handleLogout = () => {
      console.log("🧹 [ProgressContext] Clearing progress data on logout");
      latestProgress.current = null;
      setProgress(null);
      setLastFetched(0);
      setError(null);
    };

    window.addEventListener(DOCTOR_LOGOUT_EVENT, handleLogout);
    return () => {
      window.removeEventListener(DOCTOR_LOGOUT_EVENT, handleLogout);
    };
  }, []);

  const value: ProgressContextType = {
    progress,
    isLoading,
    error,
    lastFetched,
    doctorInfo,
    signup,
    clinicId,
    hasClinic,
    refreshProgress,
    clearError,
    getRequestByType,
    getRequestById,
    hasActiveRequest,
    isProfileComplete,
    isDocumentsComplete,
    currentSignupStage,
  };

  return <ProgressContext.Provider value={value}>{children}</ProgressContext.Provider>;
};

// Hook to use progress context
export const useProgress = (): ProgressContextType => {
  const context = useContext(ProgressContext);
  if (context === undefined) {
    throw new Error("useProgress must be used within a ProgressProvider");
  }
  return context;
};

// Hook with auto-refresh on mount
export const useProgressWithAutoRefresh = (autoRefresh = true) => {
  const context = useProgress();

  useEffect(() => {
    if (autoRefresh) {
      context.refreshProgress();
    }
  }, [autoRefresh]); // eslint-disable-line react-hooks/exhaustive-deps

  return context;
};

export default ProgressContext;
